import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { AccountAvatar } from '../accounts/AccountAvatar';
import { TaskFormModal } from '../tasks/TaskFormModal';
import { TaskDetailModal } from './TaskDetailModal';
import { useCrmData } from '../../contexts/CrmDataContext';
import { usePortalPath } from '../../contexts/PortalContext';
import { useMaxWidth } from '../../hooks/useMaxWidth';
import { canCreatePersonalCrmRecords } from '../../lib/roleAccess';
import { taskAccountDisplay } from '../../lib/taskAccountDisplay';
import { completeTaskAndMarkVisited } from '../../lib/taskVisitationSync';
import type { Client, Task } from '../../types/crm';
import { clientById, userCanAccessClient, userCanWriteOwnedRecord } from '../../utils/crmHelpers';
import { formatSingaporeDate, formatSingaporeTime } from '../../utils/dateTime';
import { singaporeCalendarParts, singaporeYmdKey } from '../../utils/dashboardHelpers';
import { taskDisplayStatus, taskStatusModifier } from '../../utils/taskHelpers';

interface DashboardTaskListProps {
  tasks: Task[];
  selectedYmd: string | null;
  /** YYYY-MM — when no day is selected, list tasks due in this calendar month. */
  calendarMonthKey?: string | null;
  /** Free-text filter (title, notes, account, status). */
  searchQuery?: string;
  /** Render without outer card — used inside tab panel. */
  embedded?: boolean;
}

type DayGroup = {
  ymd: string | null;
  label: string;
  tasks: Task[];
  dayStatus: 'Due' | 'Open' | null;
};

type DayStatus = NonNullable<DayGroup['dayStatus']>;

/** Inclusive window: today and the previous 4 days (5 days total). */
const MOBILE_RECENT_DAY_SPAN = 5;

function notesPreview(description?: string | null, maxLen = 100): string | null {
  const text = description?.trim().replace(/\s+/g, ' ');
  if (!text) return null;
  return text.length > maxLen ? `${text.slice(0, maxLen - 1)}…` : text;
}

function singaporeTodayYmd(): string {
  const { year, month, day } = singaporeCalendarParts();
  return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}

/** Shift a YYYY-MM-DD by whole calendar days (UTC date math avoids DST quirks). */
function shiftYmd(ymd: string, deltaDays: number): string {
  const [y, m, d] = ymd.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d + deltaDays));
  return `${dt.getUTCFullYear()}-${String(dt.getUTCMonth() + 1).padStart(2, '0')}-${String(
    dt.getUTCDate(),
  ).padStart(2, '0')}`;
}

function dayGroupStatus(dayTasks: Task[]): DayStatus | null {
  const open = dayTasks.filter((task) => (task.status ?? 'To Do') !== 'Done');
  if (open.length === 0) return null;
  if (open.some((task) => taskDisplayStatus(task) === 'Due')) return 'Due';
  return 'Open';
}

function groupTasksByDueDay(tasks: Task[]): DayGroup[] {
  const map = new Map<string, Task[]>();
  const undated: Task[] = [];

  for (const task of tasks) {
    const key = singaporeYmdKey(task.due_date);
    if (!key) {
      undated.push(task);
      continue;
    }
    const list = map.get(key) ?? [];
    list.push(task);
    map.set(key, list);
  }

  const groups: DayGroup[] = [...map.entries()]
    .sort(([a], [b]) => b.localeCompare(a))
    .map(([ymd, dayTasks]) => ({
      ymd,
      label: formatSingaporeDate(`${ymd}T12:00:00+08:00`),
      tasks: dayTasks,
      dayStatus: dayGroupStatus(dayTasks),
    }));

  if (undated.length > 0) {
    groups.push({
      ymd: null,
      label: 'No due date',
      tasks: undated,
      dayStatus: dayGroupStatus(undated),
    });
  }

  return groups;
}

function groupKey(group: DayGroup): string {
  return group.ymd ?? '__none__';
}

function partitionMobileDayGroups(
  groups: DayGroup[],
  todayYmd: string,
): { primary: DayGroup[]; more: DayGroup[] } {
  const cutoff = shiftYmd(todayYmd, -(MOBILE_RECENT_DAY_SPAN - 1));
  const upcoming: DayGroup[] = [];
  const recent: DayGroup[] = [];
  const older: DayGroup[] = [];

  for (const group of groups) {
    if (!group.ymd) {
      older.push(group);
      continue;
    }
    if (group.ymd > todayYmd) upcoming.push(group);
    else if (group.ymd >= cutoff) recent.push(group);
    else older.push(group);
  }

  upcoming.sort((a, b) => (a.ymd ?? '').localeCompare(b.ymd ?? ''));
  recent.sort((a, b) => (b.ymd ?? '').localeCompare(a.ymd ?? ''));
  older.sort((a, b) => {
    if (!a.ymd) return 1;
    if (!b.ymd) return -1;
    return b.ymd.localeCompare(a.ymd);
  });

  return { primary: [...upcoming, ...recent], more: older };
}

export function DashboardTaskList({
  tasks,
  selectedYmd,
  calendarMonthKey = null,
  searchQuery = '',
  embedded = false,
}: DashboardTaskListProps) {
  const tasksPath = usePortalPath('tasks');
  const isMobile = useMaxWidth(900);
  const {
    data,
    markTaskDoneLocal,
    effectiveRole,
    username,
    ownerLabel,
    supervisorTeam,
    actorSalespersonCodes,
    departmentSlug,
  } = useCrmData();
  const [selectedTask, setSelectedTask] = useState<Task | null>(null);
  const [editTask, setEditTask] = useState<Task | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [expandedKeys, setExpandedKeys] = useState<Set<string>>(() => new Set());
  const [moreOpen, setMoreOpen] = useState(false);
  /** Reset expansion only when calendar context changes — not on every `groups` refresh. */
  const expandSeedKeyRef = useRef('');
  const overdueMergedRef = useRef(false);

  const todayYmd = useMemo(() => singaporeTodayYmd(), []);
  const canCreateTask = canCreatePersonalCrmRecords(effectiveRole);
  const search = searchQuery.trim().toLowerCase();
  const useMobileWindow = isMobile && !selectedYmd && !search;

  /** Full client catalog for labels — scoped `clients` alone hides foreign accounts as "No account". */
  const allClientMap = useMemo(() => new Map(data.clients.map((c) => [c.id, c])), [data.clients]);

  const filtered = useMemo(() => {
    let list = tasks;
    if (selectedYmd) {
      list = list.filter((t) => singaporeYmdKey(t.due_date) === selectedYmd);
    } else if (search) {
      // Search across all tasks (ignore month window).
      list = list;
    } else if (isMobile) {
      list = list;
    } else if (calendarMonthKey) {
      list = list.filter((t) => {
        const key = singaporeYmdKey(t.due_date);
        return key != null && key.slice(0, 7) === calendarMonthKey;
      });
    }

    if (!search) return list;

    return list.filter((t) => {
      const client = t.client_id ? allClientMap.get(t.client_id) : undefined;
      const account = taskAccountDisplay(t, client);
      const haystack = [
        t.title,
        t.description,
        t.status,
        taskDisplayStatus(t),
        account.summaryLine,
        client?.company_name,
        client?.code,
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(search);
    });
  }, [tasks, selectedYmd, calendarMonthKey, isMobile, search, allClientMap]);

  const groups = useMemo(() => groupTasksByDueDay(filtered), [filtered]);

  const { primaryGroups, moreGroups } = useMemo(() => {
    if (!useMobileWindow) return { primaryGroups: groups, moreGroups: [] as DayGroup[] };
    const { primary, more } = partitionMobileDayGroups(groups, todayYmd);
    return { primaryGroups: primary, moreGroups: more };
  }, [groups, todayYmd, useMobileWindow]);

  useEffect(() => {
    const seedKey = `${selectedYmd ?? ''}|${calendarMonthKey ?? ''}|${isMobile ? 'm' : 'd'}`;
    const contextChanged = expandSeedKeyRef.current !== seedKey;

    if (contextChanged) {
      expandSeedKeyRef.current = seedKey;
      overdueMergedRef.current = false;

      if (selectedYmd) {
        setMoreOpen(false);
        setExpandedKeys(new Set([selectedYmd]));
        return;
      }

      if (!isMobile) {
        setMoreOpen(false);
        setExpandedKeys(new Set([todayYmd]));
        return;
      }

      // Mobile: seed today; overdue days merge below once groups are ready.
      setExpandedKeys(new Set([todayYmd]));
      setMoreOpen(false);
    }

    if (selectedYmd || !isMobile) return;
    if (overdueMergedRef.current || groups.length === 0) return;

    overdueMergedRef.current = true;
    const cutoff = shiftYmd(todayYmd, -(MOBILE_RECENT_DAY_SPAN - 1));
    let openMore = false;
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      next.add(todayYmd);
      for (const group of groups) {
        if (group.dayStatus !== 'Due') continue;
        next.add(groupKey(group));
        if (!group.ymd || group.ymd < cutoff) openMore = true;
      }
      return next;
    });
    if (openMore) setMoreOpen(true);
  }, [selectedYmd, calendarMonthKey, todayYmd, isMobile, groups]);

  function canEditTask(task: Task): boolean {
    return userCanWriteOwnedRecord(task.owner_id, effectiveRole, username, data.users, supervisorTeam, {
      departmentSlug,
      clientId: task.client_id,
      clients: data.clients,
      actorSalespersonCodes,
    });
  }

  function canOpenTaskAccount(client: Client | undefined): boolean {
    return userCanAccessClient(
      client,
      effectiveRole,
      username,
      data.users,
      supervisorTeam,
      actorSalespersonCodes,
      departmentSlug,
    );
  }

  function openDetail(task: Task) {
    setSelectedTask(task);
  }

  function closeDetail() {
    setSelectedTask(null);
  }

  function openEditFromDetail() {
    if (!selectedTask) return;
    setEditTask(selectedTask);
    setSelectedTask(null);
  }

  async function handleDoneFromDetail() {
    if (!selectedTask) return;
    setCompleting(true);
    try {
      await completeTaskAndMarkVisited(selectedTask);
      markTaskDoneLocal(selectedTask.id);
      setSelectedTask(null);
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Could not mark task done.');
    } finally {
      setCompleting(false);
    }
  }

  function toggleGroup(key: string) {
    setExpandedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }

  const detailClient = selectedTask
    ? clientById(data.clients, selectedTask.client_id)
    : undefined;
  const detailCanOpenAccount = canOpenTaskAccount(detailClient);
  const detailMinute = useMemo(() => {
    if (!selectedTask?.meeting_minute_id) return null;
    return data.meetingMinutes.find((m) => m.id === selectedTask.meeting_minute_id) ?? null;
  }, [data.meetingMinutes, selectedTask?.meeting_minute_id]);

  const emptyMessage = search
    ? 'No tasks match your search.'
    : selectedYmd
      ? 'No tasks due on this day.'
      : calendarMonthKey
        ? 'No tasks due in this month.'
        : 'No tasks yet.';

  const moreTaskCount = moreGroups.reduce((sum, g) => sum + g.tasks.length, 0);

  function renderDayGroup(group: DayGroup) {
    const key = groupKey(group);
    const expanded = expandedKeys.has(key);
    const count = group.tasks.length;
    return (
      <section key={key} className={`dashboard-task-day${expanded ? ' is-expanded' : ''}`}>
        <button
          type="button"
          className="dashboard-task-day__toggle"
          aria-expanded={expanded}
          onClick={() => toggleGroup(key)}
        >
          <span className="dashboard-task-day__chevron" aria-hidden>
            {expanded ? '▾' : '▸'}
          </span>
          <span className="dashboard-task-day__date">{group.label}</span>
          {group.ymd === todayYmd ? (
            <span className="dashboard-task-day__today">Today</span>
          ) : null}
          {group.ymd && group.ymd > todayYmd ? (
            <span className="dashboard-task-day__upcoming">Upcoming</span>
          ) : null}
          {group.dayStatus ? (
            <span
              className={`dashboard-task-day__status task-status ${
                group.dayStatus === 'Due' ? 'task-status--overdue' : 'task-status--todo'
              }`}
            >
              {group.dayStatus}
            </span>
          ) : null}
          <span className="dashboard-task-day__count">
            {count} task{count === 1 ? '' : 's'}
          </span>
        </button>

        {expanded ? (
          <ul className="dashboard-task-list dashboard-task-list--day">
            {group.tasks.map((t) => {
              const display = taskDisplayStatus(t);
              const client = t.client_id ? allClientMap.get(t.client_id) : undefined;
              const account = taskAccountDisplay(t, client);
              const preview = notesPreview(t.description);
              const dueTime = t.due_date ? formatSingaporeTime(t.due_date) : '';
              const customerLine =
                account.summaryLine && account.summaryLine !== 'No account'
                  ? account.summaryLine
                  : null;

              return (
                <li key={t.id}>
                  <button
                    type="button"
                    className="dashboard-task-row dashboard-task-row--time-only"
                    onClick={() => openDetail(t)}
                  >
                    <span
                      className={`dashboard-task-row__status task-status ${taskStatusModifier(display)}`}
                    >
                      {display}
                    </span>
                    <span className="dashboard-task-row__logo">
                      {client ? (
                        <AccountAvatar client={client} size="sm" />
                      ) : (
                        <span
                          className="account-avatar account-avatar--sm dashboard-task-row__logo-na"
                          aria-hidden
                        >
                          <span className="account-avatar__initials">NA</span>
                        </span>
                      )}
                    </span>
                    <div className="dashboard-task-row__main">
                      <span className="dashboard-task-row__title">{t.title}</span>
                      {customerLine ? (
                        <span className="dashboard-task-row__customer">{customerLine}</span>
                      ) : (
                        <span className="dashboard-task-row__customer dashboard-task-row__customer--empty">
                          No account
                        </span>
                      )}
                      {preview ? (
                        <span className="dashboard-task-row__notes">{preview}</span>
                      ) : (
                        <span className="dashboard-task-row__notes dashboard-task-row__notes--empty">
                          No notes
                        </span>
                      )}
                    </div>
                    <span
                      className={`dashboard-task-row__when${
                        display === 'Due' ? ' dashboard-task-row__when--overdue' : ''
                      }`}
                    >
                      {dueTime ? (
                        <span className="dashboard-task-row__time">{dueTime}</span>
                      ) : (
                        <span className="dashboard-task-row__when--empty">—</span>
                      )}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        ) : null}
      </section>
    );
  }

  const body = (
    <>
      {!embedded ? (
        <div className="dashboard-panel__head dashboard-task-list__head">
          <h2 className="detail-section-title">Tasks</h2>
          {canCreateTask ? (
            <button
              type="button"
              className="btn-primary btn-sm"
              aria-label="Add task"
              title="Add task"
              onClick={() => setCreateOpen(true)}
            >
              +
            </button>
          ) : null}
        </div>
      ) : null}

      {groups.length === 0 ? (
        <p className="detail-text dashboard-panel__empty">{emptyMessage}</p>
      ) : (
        <div className="dashboard-task-days">
          {primaryGroups.map((group) => renderDayGroup(group))}

          {moreGroups.length > 0 ? (
            <section className={`dashboard-task-more${moreOpen ? ' is-expanded' : ''}`}>
              <button
                type="button"
                className="dashboard-task-more__toggle"
                aria-expanded={moreOpen}
                onClick={() => setMoreOpen((open) => !open)}
              >
                <span className="dashboard-task-day__chevron" aria-hidden>
                  {moreOpen ? '▾' : '▸'}
                </span>
                <span className="dashboard-task-more__label">More</span>
                <span className="dashboard-task-day__count">
                  {moreTaskCount} task{moreTaskCount === 1 ? '' : 's'} · {moreGroups.length} day
                  {moreGroups.length === 1 ? '' : 's'}
                </span>
              </button>
              {moreOpen ? (
                <div className="dashboard-task-more__days">
                  {moreGroups.map((group) => renderDayGroup(group))}
                </div>
              ) : null}
            </section>
          ) : null}
        </div>
      )}

      <Link to={tasksPath} className="dashboard-panel__footer-link">
        View all tasks →
      </Link>
    </>
  );

  return (
    <>
      {embedded ? (
        <div className="dashboard-task-list-embed">{body}</div>
      ) : (
        <section className="card dashboard-panel dashboard-panel--tasks">{body}</section>
      )}

      <TaskDetailModal
        open={Boolean(selectedTask)}
        task={selectedTask}
        client={detailClient}
        canOpenAccount={detailCanOpenAccount}
        meetingMinute={detailMinute}
        ownerName={selectedTask ? ownerLabel(selectedTask.owner_id) : '—'}
        canEdit={selectedTask ? canEditTask(selectedTask) : false}
        completing={completing}
        onClose={closeDetail}
        onEdit={openEditFromDetail}
        onDone={() => void handleDoneFromDetail()}
      />

      <TaskFormModal
        open={Boolean(editTask)}
        task={editTask}
        onClose={() => setEditTask(null)}
      />

      <TaskFormModal open={createOpen} onClose={() => setCreateOpen(false)} />
    </>
  );
}
